package org.matthewo.schema.games

import org.matthewo.api.MatchOutcome
import org.matthewo.api.MetricsBioBuzzAPI
import org.matthewo.math.Gaussian
import org.matthewo.math.minus
import org.matthewo.math.plus
import org.matthewo.math.scaledSigmoid
import org.matthewo.schema.*
import org.matthewo.schema.games.MetricsBioBuzz.Companion.TIP
import org.matthewo.symbolprocessor.GameSchema
import org.matthewo.symbolprocessor.Metric
import org.matthewo.symbolprocessor.Period

@GameSchema(
    year = 2026,
    title = "BioBuzz",
    allianceSize = 2,
    customDatabaseScore = true,
    hasRankingDetails = true,
    scoreDetailsClass = "BioBuzzScoreDetails",
)
interface MetricsBioBuzz<T : Number> : StandardMetrics<T>, RankingMetrics<T>, BonusRPProvider {
    override val tbp1 get() = npTotal


    @Metric(Period.ALL, "Foul")
    override val penalty: T

    val minorFoul: T

    val majorFoul: T


    @Metric(Period.AUTO, "Tip", pointValue = TIP.toDouble())
    val apTip: T

    @Metric(Period.AUTO, "Leave", pointValue = LEAVE.toDouble())
    val apLeave: T

    @Metric(Period.AUTO, "Park", pointValue = PARK.toDouble())
    val apPark: T


    @Metric(Period.TELEOP, "Tip", pointValue = TIP.toDouble())
    val tpTip: T

    @Metric(Period.TELEOP, "Hive", pointValue = HIVE_ELEMENT.toDouble())
    val tpHive: T

    @Metric(Period.TELEOP, "Garden", pointValue = GARDEN_ELEMENT.toDouble())
    val tpGarden: T


    @Metric(Period.ENDGAME, "Flower Bottom", pointValue = FLOWER_BOTTOM.toDouble())
    val epFlowerBottom: T

    @Metric(Period.ENDGAME, "Flower Owned", pointValue = FLOWER_OWNED_ELEMENT.toDouble())
    val epFlowerOwned: T

    @Metric(Period.ENDGAME, "Park", pointValue = PARK.toDouble())
    val epPark: T


    @Metric(Period.ALL, "Bonus RP")
    val rpBonus: T

    @Metric(Period.ALL, "Swarm RP")
    val rpSwarm: T

    @Metric(Period.ALL, "Pollinator 1 RP")
    val rpPollinator1: T

    @Metric(Period.ALL, "Pollinator 2 RP")
    val rpPollinator2: T

    override val bonusRP: List<Boolean>
        get() = [rpSwarm, rpPollinator1, rpPollinator2].map { it.toDouble() >= 100 }


    companion object {
        const val TIP = 20
        const val LEAVE = 3
        const val PARK = 5
        const val HIVE_ELEMENT = 2
        const val GARDEN_ELEMENT = 1
        const val FLOWER_BOTTOM = 5
        const val FLOWER_OWNED_ELEMENT = 2
        const val MINOR_FOUL = 5
        const val MAJOR_FOUL = 20
    }
}

interface BioBuzzScoreDetails {
    val esRedFlower: Flower
    val esBlueFlower: Flower
    val esScoringFlower: Flower
    val esAudienceFlower: Flower

    typealias Flower = List<Element>

    enum class Element(val code: String) {
        POLLEN("P"),
        RED_NECTAR("R"),
        BLUE_NECTAR("B");

        companion object {
            fun fromCode(code: String) = entries.firstOrNull { it.code == code }
                ?: error("Invalid code: $code")
        }
    }
}

fun RPLevel.swarm() = when (this) {
    WORLDS -> TODO("Championship swarm")
    CHAMPIONSHIP -> TODO("Regional swarm")
    QUALIFIER -> 16
}

fun RPLevel.pollinator1Threshold() = TIP * when (this) {
    WORLDS -> TODO("Championship pollinator 1 threshold")
    CHAMPIONSHIP -> TODO("Regional pollinator 1 threshold")
    QUALIFIER -> 4
}

fun RPLevel.pollinator2Threshold() = TIP * when (this) {
    WORLDS -> TODO("Championship pollinator 2 threshold")
    CHAMPIONSHIP -> TODO("Regional pollinator 2 threshold")
    QUALIFIER -> 7
}

fun <T : Number> MutableMetricsBioBuzz<T>.computeTotals() {
    rpBonus = rpSwarm + rpPollinator1 + rpPollinator2
    auto = apTip + apLeave + apPark
    teleop = tpTip + tpHive + tpGarden
    endgame = epFlowerBottom + epFlowerOwned + epPark
    npTotal = auto + teleop + endgame
    penalty = minorFoul + majorFoul
    total = npTotal - penalty
}

object BioBuzzRankingDetails : RankingDetails<MetricsBioBuzz<*>> {
    override fun combine(team1: MetricsBioBuzz<*>, team2: MetricsBioBuzz<*>) = MetricsBioBuzzAPI<Double>(team1, team2)

    override fun winProbability(alliance: MetricsBioBuzz<*>?, opponent: MetricsBioBuzz<*>?, playedFactor: Double): Double {
        if (null == alliance || null == opponent) return 0.5
        val a = alliance.npTotal.toDouble()
        val b = opponent.npTotal.toDouble()
        val diff = (a - b) / (a + b)
        if (!diff.isFinite()) return 0.5
        val predictor = when {
            else -> scaledSigmoid(4.0, 0.7)
        }
        return predictor(diff)
    }

    override fun rp(outcome: MatchOutcome, score: MetricsBioBuzz<*>?, project: Boolean): Number {
        val match = Gaussian.next(0.5, if (outcome == MatchOutcome.WIN) 0.25 else -0.25) + 1
        val bonus =
            if (project) score?.run {
                val rpSwarm = Gaussian.next(20.0, rpSwarm.toDouble() * match) > 100.0
                val rpPollinator1 = Gaussian.next(30.0, rpPollinator1.toDouble() * match) > 100.0
                val rpPollinator2 = Gaussian.next(30.0, rpPollinator2.toDouble() * match) > 100.0
                [rpSwarm, rpPollinator1, rpPollinator2].sumOf { if (it) 1 else 0 }
            } ?: 0
            else score?.bonusRP?.sumOf { if (it) 1 else 0 } ?: 0
        val wlt = when (outcome) {
            MatchOutcome.WIN -> 3
            MatchOutcome.TIE -> 1
            MatchOutcome.LOSS -> 0
        }
        return bonus + wlt
    }

    override fun tbp1(score: MetricsBioBuzz<*>) = score.npTotal
}
